import React, { memo, useMemo, useCallback } from 'react';
import { getMutedColor, truncateLabel } from './utils';
import { calculateBranchLabelPosition } from './labelPositioning';
import { getPathEndpoint, getPathEndSegment } from '../../utils/pathGeometry';
import { extractCornerWaypoints } from '../../layout/elkEdgePathAdapter';
import { buildNotePointerPolygon } from '../../utils/noteStemPath';

/**
 * Edge Component
 * 
 * Renders a single dependency edge with visual styling based on state.
 * Decision node branches (yes/no) are colored green/red with Y/N labels at midpoint.
 * Blocking edges are dashed (regardless of branch color).
 * Shows an insert button (+) at midpoint on hover for inserting nodes.
 * 
 * PERFORMANCE: Memoized to prevent re-renders during pan/zoom.
 * Label position uses smart positioning that follows the actual path and avoids nodes.
 */
const EdgeComponent = ({ 
  edge, 
  pathD, // Pre-computed path - avoids recalculation
  isSelected, 
  isBlocking, 
  isTargetPending,
  onEdgeClick,
  onEdgeArrowDragStart,
  isReconnecting = false,
  isFadingOut,
  isFadingIn,
}) => {
  
  // Check if this is a decision node branch
  const isNoteStem = Boolean(edge.isNoteStem);
  const isBranchEdge = !isNoteStem && edge.isFromDecision && edge.branch;
  const branchColor = useMemo(() => isBranchEdge ? {
    solid: edge.branchColor || '#a78bfa',
    muted: getMutedColor(edge.branchColor || '#a78bfa'),
  } : null, [isBranchEdge, edge.branchColor]);
  
  // Smart label position - follows actual path and avoids nodes
  const labelPos = useMemo(() => {
    if (!isBranchEdge || !pathD) return null;
    return calculateBranchLabelPosition(pathD, edge.allNodes || []);
  }, [isBranchEdge, pathD, edge.allNodes]);
  
  // PERFORMANCE: Memoize style calculations
  const { stroke, markerEnd, strokeDasharray, strokeWidth } = useMemo(() => {
    let stroke = 'rgba(255, 255, 255, 0.7)';
    let markerEnd = 'url(#arrowhead)';
    let strokeDasharray = 'none';
    let strokeWidth = 1;

    if (isNoteStem) {
      return {
        stroke: 'var(--note-stem, rgba(142, 180, 212, 0.62))',
        markerEnd: 'none',
        strokeDasharray: 'none',
        strokeWidth: 1.5,
      };
    }
    
    if (isSelected) {
      stroke = '#ef4444';
      markerEnd = 'url(#arrowhead-selected)';
      strokeWidth = 2;
    } else if (isBranchEdge) {
      if (isBlocking) {
        stroke = branchColor.muted;
        markerEnd = 'url(#arrowhead-blocking)';
        strokeDasharray = '4 4';
      } else {
        stroke = branchColor.solid;
        markerEnd = 'url(#arrowhead)';
      }
    } else if (isBlocking) {
      stroke = 'rgba(255, 255, 255, 0.35)';
      markerEnd = 'url(#arrowhead-blocking)';
      strokeDasharray = '4 4';
    }
    
    return { stroke, markerEnd, strokeDasharray, strokeWidth };
  }, [isNoteStem, isSelected, isBranchEdge, isBlocking, branchColor]);
  
  // PERFORMANCE: Memoize class names (removed isHovered - now CSS-only)
  const edgeClasses = useMemo(() => [
    'flowchart__edge',
    `flowchart__edge--id-${edge.id}`, // ID for matching with HTML insert button
    isSelected && 'flowchart__edge--selected',
    isBlocking && 'flowchart__edge--blocking',
    isBranchEdge && `flowchart__edge--branch-${edge.branch}`,
    isNoteStem && 'flowchart__edge--note-stem',
    isFadingOut && 'flowchart__edge--fading-out',
    isFadingIn && 'flowchart__edge--fading-in',
  ].filter(Boolean).join(' '), [edge.id, isSelected, isBlocking, isBranchEdge, isNoteStem, edge.branch, isFadingOut, isFadingIn]);
  
  // Label color matches the edge
  const labelColor = isBranchEdge ? (isBlocking ? branchColor?.muted : branchColor?.solid) : stroke;

  const sourcePoint = useMemo(() => {
    if (!pathD) return null;
    return getPathEndpoint(pathD, 'start');
  }, [pathD]);

  const arrowHitPath = useMemo(() => {
    if (isNoteStem || !pathD || !onEdgeArrowDragStart) return null;
    return getPathEndSegment(pathD, 14);
  }, [isNoteStem, pathD, onEdgeArrowDragStart]);

  const notePointer = useMemo(() => {
    if (!isNoteStem || !pathD) return null;
    const waypoints = extractCornerWaypoints(pathD);
    if (waypoints.length < 2) return null;
    return buildNotePointerPolygon(
      waypoints[waypoints.length - 1],
      waypoints[waypoints.length - 2],
    );
  }, [isNoteStem, pathD]);

  const handleArrowMouseDown = useCallback((e) => {
    if (!onEdgeArrowDragStart) return;
    onEdgeArrowDragStart(edge, e, sourcePoint);
  }, [edge, onEdgeArrowDragStart, sourcePoint]);

  if (isReconnecting) {
    return null;
  }

  return (
    <g className={edgeClasses}>
      <path
        d={pathD}
        fill="none"
        stroke={stroke}
        strokeWidth={strokeWidth}
        strokeDasharray={strokeDasharray}
        markerEnd={markerEnd}
        className="flowchart__edge-path"
      />
      {notePointer && (
        <polygon
          className="flowchart__note-stem-pointer"
          points={notePointer}
        />
      )}
      {!isNoteStem && (
        <path
          d={pathD}
          fill="none"
          stroke="transparent"
          onClick={(e) => onEdgeClick(edge, e)}
          className="flowchart__edge-hitbox"
          style={{ cursor: 'pointer' }}
        />
      )}

      {arrowHitPath && (
        <path
          d={arrowHitPath}
          fill="none"
          stroke="transparent"
          className="flowchart__edge-arrow-hit"
          onMouseDown={handleArrowMouseDown}
        />
      )}
      
      {/* Insert button is rendered as HTML in EdgeInsertButtons component */}
      {/* This allows it to use CSS variable --inverse-zoom for instant scaling */}
      
      {/* Branch label - simplified for performance */}
      {isBranchEdge && labelPos && (
        <g 
          className="flowchart__edge-label" 
          style={{ pointerEvents: 'none' }}
          transform={`translate(${labelPos.x}, ${labelPos.y})`}
        >
          <rect
            x={-20}
            y={-8}
            width={40}
            height={16}
            rx={8}
            fill="var(--bg-primary)"
            stroke={labelColor}
            strokeWidth="1"
          />
          <text
            x="0"
            y="0.5"
            textAnchor="middle"
            dominantBaseline="central"
            fill={labelColor}
            fontSize="9"
            fontWeight="600"
          >
            {truncateLabel(edge.branchLabel, 6)}
          </text>
        </g>
      )}
    </g>
  );
};

// PERFORMANCE: Memoize Edge component to prevent re-renders during pan/zoom
// Insert button is now handled by EdgeInsertButtons component (HTML-based)
const Edge = memo(EdgeComponent, (prevProps, nextProps) => {
  // Only re-render if these specific props change
  if (prevProps.pathD !== nextProps.pathD) return false;
  if (prevProps.isSelected !== nextProps.isSelected) return false;
  if (prevProps.isReconnecting !== nextProps.isReconnecting) return false;
  if (prevProps.isBlocking !== nextProps.isBlocking) return false;
  if (prevProps.isFadingOut !== nextProps.isFadingOut) return false;
  if (prevProps.isFadingIn !== nextProps.isFadingIn) return false;
  if (prevProps.edge.branchColor !== nextProps.edge.branchColor) return false;
  if (prevProps.edge.branchLabel !== nextProps.edge.branchLabel) return false;
  if (prevProps.edge.id !== nextProps.edge.id) return false;
  if (prevProps.edge.isNoteStem !== nextProps.edge.isNoteStem) return false;
  // allNodes affects label positioning - compare by reference (regenerated when positions change)
  if (prevProps.edge.allNodes !== nextProps.edge.allNodes) return false;
  return true;
});

export default Edge;

